import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { of, switchMap, catchError } from 'rxjs';
import { JobRequestsService } from '../../services/job-requests.service';
import { DesignService } from '../../services/design.service';
import { ServiceCompanyService } from '../../services/service-company.service';
import { JobRequestReportDto } from '../../models/job-request-report-dto.interface';
import {
  ContentFieldDto,
  DesignIterationDto,
  DesignMaterialRowDto,
  DesignTestInputDto,
  DesignTestOutputDto,
  FormulationComponentDto,
  JobRequestIterationsDto
} from '../../models/job-request-iterations-dto.interface';
import { ServiceCompanyMudCheckPropertiesDto } from '../../models/service-company-dto.interface';
import { OverviewRowDto } from '../../models/job-request-overview-dto.interface';
import { VerticalTestComponentsModule } from '../job-request-column-report/vertical-test-components.module';
import { reportPageEnds, ReportBlock } from '../job-request-column-report/report-pagination';
import { MudCheckReportComponent } from './mud-check-report.component';
import { ApiFluidLossTestReportComponent } from './job-request-report-tests/api-fluid-loss-test-report.component';
import { DensityTestReportComponent } from './job-request-report-tests/density-test-report.component';
import { ElectricalStabilityTestReportComponent } from './job-request-report-tests/electrical-stability-test-report.component';
import { HthpFluidLossTestReportComponent } from './job-request-report-tests/hthp-fluid-loss-test-report.component';
import { HphtRheologyTestReportComponent } from './job-request-report-tests/hpht-rheology-test-report.component';
import { NafChemistryTestReportComponent } from './job-request-report-tests/naf-chemistry-test-report.component';
import { WbmChloridesTestReportComponent } from './job-request-report-tests/wbm-chlorides-test-report.component';
import { WbmChemistryTestReportComponent } from './job-request-report-tests/wbm-chemistry-test-report.component';
import { PsdTestReportComponent } from './job-request-report-tests/psd-test-report.component';
import { PptTestReportComponent } from './job-request-report-tests/ppt-test-report.component';
import { RetortTestReportComponent } from './job-request-report-tests/retort-test-report.component';
import { RheologyTestReportComponent } from './job-request-report-tests/rheology-test-report.component';
import { PycnometerTestReportComponent } from './job-request-report-tests/pycnometer-test-report.component';
import { StaticAgingTestReportComponent } from './job-request-report-tests/static-aging-test-report.component';

@Component({
  selector: 'app-job-request-report',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, VerticalTestComponentsModule, MudCheckReportComponent, ApiFluidLossTestReportComponent, DensityTestReportComponent, ElectricalStabilityTestReportComponent, HthpFluidLossTestReportComponent, HphtRheologyTestReportComponent, NafChemistryTestReportComponent, WbmChloridesTestReportComponent, WbmChemistryTestReportComponent, PsdTestReportComponent, PptTestReportComponent, RetortTestReportComponent, RheologyTestReportComponent, PycnometerTestReportComponent, StaticAgingTestReportComponent],
  templateUrl: './job-request-report.component.html',
  styleUrl: './job-request-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JobRequestReportComponent implements OnInit, OnDestroy {
  private static readonly ITERATION_JOB_REQUEST_TYPES = new Set([2, 3]);
  private static readonly WATER_DENSITY_PPG = 8.345;
  private static readonly PDF_PAGE_MARGIN_INCHES = 0;
  private static readonly EMPTY_TEST_OUTPUT: DesignTestOutputDto = {
    id: 0,
    content: null,
    isDefault: null,
    equipmentName: null,
    parsedValues: []
  };
  @ViewChild('reportDocument') private reportDocument?: ElementRef<HTMLElement>;

  protected readonly report = signal<JobRequestReportDto | null>(null);
  protected readonly iterations = signal<JobRequestIterationsDto | null>(null);
  protected readonly mudCheckProperties = signal<ServiceCompanyMudCheckPropertiesDto | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly notFound = signal(false);
  protected readonly isGeneratingPdf = signal(false);
  protected readonly pdfError = signal(false);
  protected readonly pdfPreviewUrl = signal<SafeResourceUrl | null>(null);
  protected readonly pdfDownloadUrl = signal<string | null>(null);
  protected readonly pdfFilename = signal('report.pdf');

  private readonly route = inject(ActivatedRoute);
  private readonly jobRequestsService = inject(JobRequestsService);
  private readonly designService = inject(DesignService);
  private readonly serviceCompanyService = inject(ServiceCompanyService);
  private readonly sanitizer = inject(DomSanitizer);

  ngOnInit(): void {
    const requestId = Number(this.route.snapshot.paramMap.get('id'));

    this.jobRequestsService.getReport(requestId).pipe(
      switchMap(report => {
        this.report.set(report);
        // Load design iterations for design-based reports (pilot test / cutback), not mud checks
        const designPromise = report.jobRequestTypeId !== 1
          ? this.designService.getIterations(requestId).pipe(
              switchMap(data => {
                this.iterations.set(this.filterSelectedDesigns(data));
                return of(null);
              }),
              catchError(() => of(null))
            )
          : of(null);
        
        // Load mud check properties for mud check reports
        const mudCheckPromise = report.jobRequestTypeId === 1
          ? this.serviceCompanyService.getServiceCompanyData(report.jobId).pipe(
              switchMap(data => {
                const mudCheckData = data?.mudCheckProperties?.[0] ?? null;
                this.mudCheckProperties.set(mudCheckData);
                return of(null);
              }),
              catchError(() => of(null))
            )
          : of(null);

        return designPromise.pipe(switchMap(() => mudCheckPromise));
      })
    ).subscribe({
      next: () => {
        const report = this.report();
        this.isLoading.set(false);
        if (report) {
          this.pdfFilename.set(this.createFilename(report));
          this.queuePdfGeneration();
        }
      },
      error: () => {
        this.notFound.set(true);
        this.isLoading.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.revokePdfUrl();
  }

  protected regeneratePdf(): void {
    this.revokePdfUrl();
    this.pdfPreviewUrl.set(null);
    this.pdfDownloadUrl.set(null);
    this.queuePdfGeneration();
  }

  protected openPdf(): void {
    const url = this.pdfDownloadUrl();
    if (url) window.open(`${url}#zoom=100`, '_blank', 'noopener,noreferrer');
  }

  protected isMudCheck(report: JobRequestReportDto): boolean {
    return report.jobRequestTypeId === 1;
  }

  protected displayLabel(label: string): string {
    return label
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\b(run|rpm)(\d+)\b/gi, '$1 $2')
      .replace(/\b\w/g, character => character.toUpperCase())
      .replace(/\bRpm\b/g, 'RPM')
      .replace(/\bEs\b/g, 'ES')
      .replace(/\bSg\b/g, 'SG')
      .replace(/\bHthp\b/g, 'HTHP')
      .replace(/\bAsg\b/g, 'ASG');
  }

  protected entries(
    values?: Record<string, string | null>,
    excludedValues?: Record<string, string | null>
  ): Array<{ label: string; value: string | null }> {
    const excludedKeys = new Set(Object.keys(excludedValues ?? {}).map(key => key.toLowerCase()));

    return Object.entries(values ?? {})
      .filter(([key, value]) =>
        !excludedKeys.has(key.toLowerCase())
        && !key.includes(':')
        && key !== 'id'
        && !key.endsWith('Id')
        && value !== null
        && value.trim() !== '')
      .map(([label, value]) => ({ label: this.displayLabel(label), value }));
  }

  protected fieldLabel(field: ContentFieldDto): string {
    return this.displayLabel(field.label);
  }

  protected testOutput(test: DesignTestInputDto): DesignTestOutputDto {
    return test.testOutputs[0] ?? JobRequestReportComponent.EMPTY_TEST_OUTPUT;
  }

  protected testResultFields(test: DesignTestInputDto, output: DesignTestOutputDto): ContentFieldDto[] {
    const fields = new Map<string, ContentFieldDto>();

    for (const field of [...test.parsedInputValues, ...output.parsedValues]) {
      if (!field.label?.trim() || !field.value?.trim()) continue;
      fields.set(this.normalizeFieldKey(field.label), field);
    }

    return Array.from(fields.values());
  }

  protected customTestRow(report: JobRequestReportDto, test: DesignTestInputDto): OverviewRowDto | undefined {
    return report.overviewGroups
      .flatMap(group => group.overviews)
      .flatMap(overview => overview.rows)
      .find(row => row.testInputId === test.id);
  }

  protected hasCustomTestDefinition(test: DesignTestInputDto, row?: OverviewRowDto): boolean {
    const definitionJson = test.customTestDefinitionJson ?? row?.customTestDefinitionJson;
    if (!definitionJson) return false;

    try {
      const definition = JSON.parse(definitionJson) as {
        inputParameters?: unknown[];
        outputParameters?: unknown[];
        table?: { columns?: unknown[] } | null;
        spreadsheet?: unknown;
      } | null;

      return !!(definition?.inputParameters?.length
        || definition?.outputParameters?.length
        || definition?.table?.columns?.length
        || definition?.spreadsheet);
    } catch {
      return false;
    }
  }

  protected componentName(component: FormulationComponentDto): string {
    return component.name || component.materialName || component.chemicalName || `Component ${component.id}`;
  }

  protected designWeight(design: DesignIterationDto, component: FormulationComponentDto): number | null {
    const material = design.materials.find((row: DesignMaterialRowDto) =>
      row.formulationComponentId === component.id);
    return material?.weight ?? null;
  }

  protected isRheologyTest(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[^a-z0-9]+/gi, '').toLowerCase();
    const values = [test.testTypeAlias, test.testTypeName, test.title].map(normalize);
    const isHphtRheology = values.some(value =>
      value.includes('hphtrheology')
      || value.includes('hthprheology')
      || value.includes('fann77'));
    if (isHphtRheology) return false;

    return test.testTypeId === 7
      || values.some(value => value === 'rheology' || value === 'rheologytest' || value.includes('rheology'));
  }

  protected isHphtRheologyTest(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[^a-z0-9]+/gi, '').toLowerCase();

    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(normalize)
      .some(value => [
        'hphtrheology',
        'hphtrheologytest',
        'hthprheology',
        'hthprheologytest',
        'fann77',
        'fann77test',
        'fann77rheology'
      ].includes(value));
  }

  protected hphtRheologyReferenceTest(
    design: DesignIterationDto,
    test: DesignTestInputDto
  ): DesignTestInputDto | null {
    const associatedGroup = design.testGroups.find(group =>
      group.testInputs.some(candidate => candidate.id === test.id));
    const candidates = [
      ...(associatedGroup?.testInputs ?? []),
      ...design.testGroups
        .filter(group => group !== associatedGroup)
        .flatMap(group => group.testInputs)
    ];

    return candidates.find(candidate => candidate.id !== test.id && this.isRheologyTest(candidate)) ?? null;
  }

  protected isDensityTest(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[\s_-]+/g, '').toLowerCase();

    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(normalize)
      .some(value => value === 'density' || value === 'densitytest');
  }

  protected isPycnometerTest(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[^a-z0-9]+/gi, '').toLowerCase();

    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(normalize)
      .some(value => value === 'pycnometer' || value.includes('pycnometer'));
  }

  protected isStaticAgingTest(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[^a-z0-9]+/gi, '').toLowerCase();

    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(normalize)
      .some(value => value === 'staticaging' || value.includes('staticaging'));
  }

  protected isElectricalStabilityTest(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[\s_-]+/g, '').toLowerCase();

    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(normalize)
      .some(value => value === 'electricalstability' || value === 'electricalstabilitytest');
  }

  protected isRetortTest(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[\s_-]+/g, '').toLowerCase();

    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(normalize)
      .some(value => value === 'retort' || value === 'retorttest');
  }

  protected isApiFluidLossTest(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[\s_-]+/g, '').toLowerCase();

    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(normalize)
      .some(value => value === 'apifluidloss' || value === 'apifluidlosstest');
  }

  protected isHthpFluidLossTest(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[\s_-]+/g, '').toLowerCase();

    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(normalize)
      .some(value => [
        'hthp',
        'hphtfluidloss',
        'hphtfluidlosstest',
        'hthpfluidloss',
        'hthpfluidlosstest'
      ].includes(value));
  }

  protected isNafChemistryTest(test: DesignTestInputDto, ): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[\s_-]+/g, '').toLowerCase();

    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(normalize)
      .some(value => ['nafchemistry', 'nafchemistrytest', 'salinity', 'salinitytest'].includes(value));
  }

  protected isS3s4Chlorides(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();

    const values = [test.testTypeAlias, test.testTypeName, test.title].map(normalize);
    return values.some(v =>
      v.includes('wbmtitration') || v.includes('wbmchlorides') || v.includes('s3s4') || v.includes('s3s4chlorides')
    );
  }

  protected isPptTest(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[\s_-]+/g, '').toLowerCase();

    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(normalize)
      .some(value => ['ppt', 'ppttest', 'permeabilityplugging', 'permeabilitypluggingtest'].includes(value));
  }

  protected isPsdTest(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[\s_-]+/g, '').toLowerCase();

    return [test.testTypeAlias, test.testTypeName, test.title]
      .map(normalize)
      .some(value => ['psd', 'psdtest', 'particlesize', 'particlesizedistribution', 'particlesizedistributiontest'].includes(value));
  }

  protected isWbmChemistry(test: DesignTestInputDto): boolean {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();

    const values = [test.testTypeAlias, test.testTypeName, test.title].map(normalize);
    return values.some(v => [
      'wbmchemistry',
      'wbmchemistrytest',
      'wbmchemistryresults',
      'wbmchemistryreport',
      'wbmchem'
    ].includes(v));
  }

  protected retortMudSpecificGravity(
    iterations: JobRequestIterationsDto,
    design: DesignIterationDto,
    report: JobRequestReportDto
  ): number | null {
    const normalize = (value: string | null | undefined) =>
      (value ?? '').replace(/[\s_\-/,().:]+/g, '').toLowerCase();
    const densityOutputs = design.testGroups
      .flatMap(group => group.testInputs)
      .filter(test => this.isDensityTest(test))
      .flatMap(test => test.testOutputs);

    for (const output of densityOutputs) {
      const field = output.parsedValues.find(value =>
        ['specificgravity', 'densitygml'].includes(normalize(value.label)));
      const specificGravity = Number(field?.value);
      if (Number.isFinite(specificGravity) && specificGravity > 0) return specificGravity;
    }

    const baselineDensityPpg = iterations.baselineDensityPpg ?? report.originalDensity;
    return baselineDensityPpg !== null
      && baselineDensityPpg !== undefined
      && baselineDensityPpg > 0
      ? baselineDensityPpg / JobRequestReportComponent.WATER_DENSITY_PPG
      : null;
  }

  private filterSelectedDesigns(iterations: JobRequestIterationsDto): JobRequestIterationsDto {
    const requestedDesignIds = this.route.snapshot.queryParamMap.getAll('designid')
      .map(value => Number(value))
      .filter(value => Number.isInteger(value) && value > 0);
    if (requestedDesignIds.length === 0) return iterations;

    return {
      ...iterations,
      designs: iterations.designs.filter(design => requestedDesignIds.includes(design.id))
    };
  }

  private normalizeFieldKey(value: string): string {
    return value.replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }

  private queuePdfGeneration(): void {
    this.pdfError.set(false);
    this.isGeneratingPdf.set(true);
    requestAnimationFrame(() => requestAnimationFrame(() => void this.generatePdf()));
  }

  private async generatePdf(): Promise<void> {
    const source = this.reportDocument?.nativeElement;
    if (!source) {
      this.isGeneratingPdf.set(false);
      this.pdfError.set(true);
      return;
    }

    try {
      await document.fonts.ready;
      const fontScale = 0.8;
      const typographyProperties = [
        'font-family', 'font-size', 'font-weight', 'font-style', 'line-height', 'letter-spacing'
      ];
      const typography = [source, ...Array.from(source.querySelectorAll<HTMLElement>('*'))]
        .map(element => {
          const style = getComputedStyle(element);
          return typographyProperties.map(property => style.getPropertyValue(property));
        });
      const applyTypography = async (clonedDocument: Document): Promise<void> => {
        const clonedSource = clonedDocument.querySelector<HTMLElement>('.html2pdf__container .report-document');
        if (!clonedSource) throw new Error('PDF report clone is missing.');
        [clonedSource, ...Array.from(clonedSource.querySelectorAll<HTMLElement>('*'))]
          .filter(element => !element.closest('html2canvaspseudoelement'))
          .forEach((element, index) => {
            typography[index]?.forEach((value, propertyIndex) => {
              const property = typographyProperties[propertyIndex];
              const scaledValue = (property === 'font-size' || property === 'line-height') && value.endsWith('px')
                ? `${parseFloat(value) * fontScale}px`
                : value;
              element.style.setProperty(property, scaledValue, 'important');
            });
          });
        await clonedDocument.fonts.ready;
      };
      source.classList.add('pdf-capture');
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      await document.fonts.ready;

      const isMudCheck = this.report()?.jobRequestTypeId === 1;
      if (isMudCheck) {
        const { default: html2pdf } = await import('html2pdf.js');
        const pdfOptions = {
          margin: JobRequestReportComponent.PDF_PAGE_MARGIN_INCHES,
          filename: this.pdfFilename(),
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            backgroundColor: '#ffffff',
            logging: false,
            scale: 2,
            useCORS: true,
            onclone: applyTypography
          },
          pagebreak: {
            mode: []
          },
          jsPDF: {
            unit: 'in',
            format: [8.5, 11],
            orientation: 'portrait'
          }
        } as any;
        const worker = html2pdf()
          .set(pdfOptions)
          .from(source)
          .toCanvas();
        const canvas = await worker.get('canvas') as HTMLCanvasElement;
        const pdf = await worker.toPdf().get('pdf') as any;

        while (pdf.getNumberOfPages() > 1) {
          pdf.deletePage(pdf.getNumberOfPages());
        }

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = JobRequestReportComponent.PDF_PAGE_MARGIN_INCHES;
        const availableWidth = pageWidth - margin * 2;
        const availableHeight = pageHeight - margin * 2;
        const canvasRatio = canvas.height / canvas.width;
        let imageWidth = availableWidth;
        let imageHeight = imageWidth * canvasRatio;

        if (imageHeight > availableHeight) {
          imageHeight = availableHeight;
          imageWidth = imageHeight / canvasRatio;
        }

        const imageX = (pageWidth - imageWidth) / 2;
        pdf.setPage(1);
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.98), 'JPEG', imageX, margin, imageWidth, imageHeight);
        const blob = pdf.output('blob') as Blob;

        this.revokePdfUrl();
        const objectUrl = URL.createObjectURL(blob);
        this.pdfDownloadUrl.set(objectUrl);
        this.pdfPreviewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(`${objectUrl}#zoom=100`));
        return;
      }

        source.classList.add('pdf-paginated');
        const { default: html2pdf } = await import('html2pdf.js');
        const margin = 0.35;
        const contentWidth = 8.5 - 2 * margin;
        const contentHeight = 11 - 2 * margin;
        let captureWidth = 0;
        let blocks: ReportBlock[] = [];
        const pdfOptions = {
          margin,
          filename: this.pdfFilename(),
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            backgroundColor: '#ffffff',
            logging: false,
            scale: 2,
            useCORS: true,
            windowWidth: 1280,
            onclone: async (clonedDocument: Document) => {
              await applyTypography(clonedDocument);
              const container = clonedDocument.querySelector<HTMLElement>('.html2pdf__container');
              if (!container) throw new Error('PDF capture container is missing.');
              const bounds = container.getBoundingClientRect();
              captureWidth = bounds.width;
              blocks = Array.from(container.querySelectorAll<HTMLElement>('.avoid-break, tr, .report-footer'))
                .map(element => {
                  const rect = element.getBoundingClientRect();
                  return { top: rect.top - bounds.top, bottom: rect.bottom - bounds.top };
                });
            }
          },
          pagebreak: {
            mode: []
          },
          jsPDF: {
            unit: 'in',
            format: [8.5, 11],
            orientation: 'portrait'
          }
        } as any;
      const canvas = await html2pdf()
        .set(pdfOptions)
        .from(source)
        .toCanvas()
        .get('canvas') as HTMLCanvasElement;
      if (captureWidth <= 0) throw new Error('PDF capture width is invalid.');

      const scale = canvas.width / captureWidth;
      const pageHeight = Math.floor(canvas.width * contentHeight / contentWidth);
      const imageHeight = pageHeight * contentWidth / canvas.width;
      const pageEnds = reportPageEnds(canvas.height, pageHeight, blocks.map(block => ({
        top: Math.round(block.top * scale),
        bottom: Math.round(block.bottom * scale)
      })));
      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = pageHeight;
      const context = pageCanvas.getContext('2d');
      if (!context) throw new Error('PDF page canvas is unavailable.');

      let pageStart = 0;
      let pdf: any;
      for (const pageEnd of pageEnds) {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        context.drawImage(canvas, 0, pageStart, canvas.width, pageEnd - pageStart,
          0, 0, canvas.width, pageEnd - pageStart);
        if (!pdf) {
          pdf = await html2pdf().set(pdfOptions).from(pageCanvas).toPdf().get('pdf');
        } else {
          pdf.addPage();
          pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.98), 'JPEG', margin, margin,
            contentWidth, imageHeight);
        }
        pageStart = pageEnd;
      }
      const blob = pdf.output('blob') as Blob;

      this.revokePdfUrl();
      const objectUrl = URL.createObjectURL(blob);
      this.pdfDownloadUrl.set(objectUrl);
      this.pdfPreviewUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(`${objectUrl}#zoom=100`));
    } catch {
      this.pdfError.set(true);
    } finally {
      source.classList.remove('pdf-capture', 'pdf-paginated');
      this.isGeneratingPdf.set(false);
    }
  }

  private revokePdfUrl(): void {
    const currentUrl = this.pdfDownloadUrl();
    if (currentUrl) URL.revokeObjectURL(currentUrl);
  }

  private createFilename(report: JobRequestReportDto): string {
    const baseName = `${report.name ?? `request-${report.id}`}-${report.title ?? 'report'}`
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();
    return `${baseName || 'report'}.pdf`;
  }
}
